import axios from 'axios';
import API_CONFIG, { getApiUrl } from '../config/api';

// Create axios instance with default configuration
const apiClient = axios.create({
  baseURL: API_CONFIG.BASE_URL,
  timeout: API_CONFIG.TIMEOUT,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor for logging
apiClient.interceptors.request.use(
  (config) => {
    console.log(`🚀 API Request: ${config.method?.toUpperCase()} ${config.url}`);
    return config;
  },
  (error) => {
    console.error('❌ API Request Error:', error);
    return Promise.reject(error);
  }
);

// Response interceptor for logging
apiClient.interceptors.response.use(
  (response) => {
    console.log(`✅ API Response: ${response.status} ${response.config.url}`);
    return response;
  },
  (error) => {
    console.error('❌ API Response Error:', error.response?.status, error.response?.data);
    return Promise.reject(error);
  }
);

// API Service functions
export const apiService = {
  // Health check
  health: async () => {
    const response = await apiClient.get(API_CONFIG.ENDPOINTS.HEALTH);
    return response.data;
  },

  // Message analysis
  analyzeMessage: async (messageData) => {
    const response = await apiClient.post(API_CONFIG.ENDPOINTS.ANALYZE, messageData);
    return response.data;
  },

  // Streaming message analysis
  analyzeMessageStream: async (messageData, onChunk) => {
    const response = await fetch(`${API_CONFIG.BASE_URL}/api/analyze/stream`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(messageData),
      // Set longer timeout for streaming analysis
      signal: AbortSignal.timeout(60000), // 60 seconds timeout
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`HTTP error! status: ${response.status}, message: ${errorText}`);
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      const chunk = decoder.decode(value);
      const lines = chunk.split('\n');

      for (const line of lines) {
        if (line.startsWith('data: ')) {
          try {
            const data = JSON.parse(line.slice(6));
            onChunk(data);
          } catch (e) {
            console.error('Error parsing streaming data:', e);
          }
        }
      }
    }
  },

  // Get messages with pagination and filtering
  getMessages: async (params = {}) => {
    const response = await apiClient.get(API_CONFIG.ENDPOINTS.MESSAGES, { params });
    return response.data;
  },

  // Get specific message
  getMessage: async (messageId) => {
    const response = await apiClient.get(`${API_CONFIG.ENDPOINTS.MESSAGES}/${messageId}`);
    return response.data;
  },

  // Update message
  updateMessage: async (messageId, updateData) => {
    const response = await apiClient.put(`${API_CONFIG.ENDPOINTS.MESSAGES}/${messageId}`, updateData);
    return response.data;
  },

  // Submit message review
  submitReview: async (messageId, reviewData) => {
    const response = await apiClient.post(`${API_CONFIG.ENDPOINTS.MESSAGES}/${messageId}/review`, reviewData);
    return response.data;
  },

  // Get statistics
  getStatistics: async () => {
    const response = await apiClient.get(API_CONFIG.ENDPOINTS.STATISTICS);
    return response.data;
  },

  // Merkle tree endpoints
  getMerkleRoot: async () => {
    const response = await apiClient.get(API_CONFIG.ENDPOINTS.MERKLE_ROOT);
    return response.data;
  },

  getMessageMerkleRoot: async (messageId) => {
    const response = await apiClient.get(`/api/merkle/message/${messageId}`);
    return response.data;
  },

  getMerkleAuditTrail: async () => {
    const response = await apiClient.get('/api/merkle/audit-trail');
    return response.data;
  },

  // Blockchain endpoints
  getBlockchainContractInfo: async () => {
    const response = await apiClient.get(API_CONFIG.ENDPOINTS.BLOCKCHAIN_CONTRACT);
    return response.data;
  },

  getBlockchainExplorerUrl: async (merkleRoot) => {
    const response = await apiClient.get(`${API_CONFIG.ENDPOINTS.BLOCKCHAIN_EXPLORER}/${merkleRoot}`);
    return response.data;
  },

  getBlockchainCompletionExplorerUrl: async (completionId) => {
    const response = await apiClient.get(`${API_CONFIG.ENDPOINTS.BLOCKCHAIN_COMPLETION_EXPLORER}/${completionId}`);
    return response.data;
  },

  // Rules endpoints
  getRules: async () => {
    const response = await apiClient.get(API_CONFIG.ENDPOINTS.RULES);
    return response.data;
  },

  getRule: async (ruleId) => {
    const response = await apiClient.get(`${API_CONFIG.ENDPOINTS.RULES}/${ruleId}`);
    return response.data;
  },

  createRule: async (ruleData) => {
    const response = await apiClient.post(API_CONFIG.ENDPOINTS.RULES, ruleData);
    return response.data;
  },

  updateRule: async (ruleId, ruleData) => {
    const response = await apiClient.put(`${API_CONFIG.ENDPOINTS.RULES}/${ruleId}`, ruleData);
    return response.data;
  },

  deleteRule: async (ruleId) => {
    const response = await apiClient.delete(`${API_CONFIG.ENDPOINTS.RULES}/${ruleId}`);
    return response.data;
  },
};

export default apiService; 